import {LoaderFunctionArgs} from 'react-router-dom';
import {runInAction} from 'mobx';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {IRouteStore} from '../../router/routing.infra';
import {RolesConstant as ROLES} from '@techsee/techsee-common/lib/constants/account.constants';
import {DashboardState} from './_contracts/DashboardState';
import {ClientRoomStorage} from './_contracts/ClientRoomStorage';
import {DashboardStores, setDashboardStore} from './dashboard.stores';
import {getDashboardLanguage, setDashboardLanguage} from '../helper/general.helper';
import {resolveEmbeddedInfo, resolveDashboardUserAndAccount, resolveRoomData} from '../helper/dashboard-resolve.helper';

const trace = getAppTracer('DashboardLoader');

async function resolvePreloadedImage(currentUser: any, url: URL): Promise<any> {
    const rootStore = getRootStore();
    const resourceId = url.searchParams.get('loadImage');
    const libraryTaggingMode = currentUser.role === ROLES.KNOWLEDGE_EXPERT;

    if (!resourceId) {
        return {};
    }

    try {
        if (libraryTaggingMode) {
            const resp = await rootStore.resourcesService.resign(resourceId);

            return {resource: resp};
        }

        const historyApiService = rootStore.historyApiService;

        await historyApiService.resignResource(resourceId, 'IMAGE');
        const resp = await historyApiService.getResourceById(resourceId, 'IMAGE');

        return {resource: resp};
    } catch (error) {
        return {error};
    }
}

async function resolvePreloadedVideo(currentUser: any, url: URL): Promise<any> {
    const rootStore = getRootStore();
    const resourceId = url.searchParams.get('loadVideo');
    const libraryTaggingMode = currentUser.role === ROLES.KNOWLEDGE_EXPERT;

    if (!resourceId) {
        return {};
    }

    try {
        if (libraryTaggingMode) {
            const resp = await rootStore.resourcesService.resign(resourceId);

            return {resource: resp};
        }

        const historyApiService = rootStore.historyApiService;

        await historyApiService.resignResource(resourceId, 'VIDEO');
        const resp = await historyApiService.getResourceById(resourceId, 'VIDEO');

        if (resp) {
            resp.isVideo = true;
            resp.video = resp.url;
        }

        return {resource: resp};
    } catch (error) {
        return {error};
    }
}

export async function dashboardRouteLoader(args: LoaderFunctionArgs): Promise<IRouteStore> {
    trace.info('Dashboard loader starting');
    const url = new URL(args.request.url);
    const rootStore = getRootStore();

    await rootStore.initRootServicesCompleted;

    const embeddedInfo = resolveEmbeddedInfo(url);

    const {currentUser, accountData} = await resolveDashboardUserAndAccount(embeddedInfo, url);

    rootStore.tsExtraLoggingService.init(accountData, rootStore.eventLogService);

    const roomData = await resolveRoomData(currentUser, accountData, embeddedInfo, url);

    const accountLanguage = getDashboardLanguage(accountData, roomData);

    setDashboardLanguage(accountLanguage);
    await rootStore.translationHelper.storeAccountCustomStrings(accountData._id);
    await rootStore.tsLocaleHelper.loadLocale(accountLanguage, accountData.companyName);

    const preloadedImage = await resolvePreloadedImage(currentUser, url);
    const preloadedVideo = await resolvePreloadedVideo(currentUser, url);

    const clientRoomStorage = new ClientRoomStorage();
    const dashboardState = new DashboardState(accountData, currentUser, roomData, clientRoomStorage);

    runInAction(() => {
        dashboardState.offlineMode = !!url.searchParams.get('offline');
        dashboardState.isOfflineRoom = roomData.offline && url.searchParams.get('offline') !== 'true';
        dashboardState.isOfflineMode = url.searchParams.get('offline') === 'true';
        dashboardState.isObserve = !!url.searchParams.get('observe');
        dashboardState.audioRequestedFromUrl = url.searchParams.get('audio') === 'yes';
        dashboardState.sophieCtx = url.searchParams.get('sophie-ctx');
        dashboardState.roomCode = url.searchParams.get('roomCode') || '';
        dashboardState.embeddedInfo = embeddedInfo;
        dashboardState.videoModeState.preloadedImage = preloadedImage;
        dashboardState.preloadedVideo = preloadedVideo;
    });

    const dashboardStores = new DashboardStores(dashboardState);

    setDashboardStore(dashboardStores);

    await dashboardStores.initStore();

    trace.info('Dashboard loader completed successfully');

    return dashboardStores;
}
